import { useState } from 'react';

import {
  Box,
  Button,
  Card,
  Divider,
  DropdownSelect,
  IconButton,
  Menu,
  Switch,
  Text,
  Tooltip,
} from '@zillow/constellation-10';
import { IconCloseFilled, IconInfoFilled } from '@zillow/constellation-icons';

import { SmartConfigMode } from '@zg-rentals/ts-rental-manager-api-client';

import { SmartAssignActivationModal } from './SmartAssignActivationModal';

import { useAutoSpotlight } from './AutoSpotlightContext';
import { OPTIMIZER_LIST } from './rabDeployment';
import { SPOTLIGHT_COPY as COPY } from './spotlightCopy';

import type { OptimizerId } from './rabDeployment';

const INFO_ICON = (
  <IconButton
    title="Info"
    size="sm"
    emphasis="bare"
    shape="circle"
    css={{ color: 'icon.muted', width: 'icon.sm', height: 'icon.sm' }}
    icon={<IconInfoFilled />}
  />
);

export function SmartAssignHeader() {
  const ctx = useAutoSpotlight();
  const [activationOpen, setActivationOpen] = useState(false);

  if (!ctx) {
    return null;
  }

  const {
    autoCount,
    availableCount,
    applyBulkTarget,
    clearSelection,
    isDisablingSmartAssign,
    isOverviewLoaded,
    isSelectionMode,
    licenses,
    onChangeOptimizer,
    optimizer,
    pinnedCount,
    planBulkTarget,
    requestDisableSmartAssign,
    selectedIds,
    smartConfigMode,
    spotlightedCount,
    hasZramManageableListings,
    toggleSelectionMode,
  } = ctx;

  const smartAssignEnabled = smartConfigMode !== null;
  const inSpotlight = spotlightedCount;
  // Only feed-managed listings → nothing for Smart Assign to manage; drives the explanatory tooltip.
  const feedManagedBlocked = !smartAssignEnabled && isOverviewLoaded && !hasZramManageableListings;
  // Block enabling for the feed-managed case, and until the overview loads so the real value can't be pre-empted.
  // Never blocks disabling.
  const enableBlocked = !smartAssignEnabled && (!isOverviewLoaded || !hasZramManageableListings);

  const handleToggle = () => {
    if (!smartAssignEnabled) {
      if (enableBlocked) {
        return;
      }
      setActivationOpen(true);
      return;
    }
    requestDisableSmartAssign();
  };

  const options = OPTIMIZER_LIST.map((o) => ({ label: o.label, value: o.id, meta: o.description }));
  const selectedCount = selectedIds.size;
  const selectedAliases = Array.from(selectedIds);
  const pinPlan = planBulkTarget(selectedAliases, 'PINNED');
  const smartAssignPlan = planBulkTarget(selectedAliases, 'SMART_ASSIGN');
  const excludePlan = planBulkTarget(selectedAliases, 'EXCLUDED');
  const hasUnknownSelection = pinPlan.unknownSourceCount > 0;
  const bulkActions: Array<{ label: string; onSelect: () => void; disabled: boolean }> = [
    {
      label: 'Pin',
      onSelect: () => applyBulkTarget(selectedAliases, 'PINNED'),
      disabled:
        hasUnknownSelection ||
        pinPlan.alreadyAtTargetCount === selectedCount ||
        (isOverviewLoaded && pinPlan.netNewPinSlots > availableCount),
    },
    {
      label: 'Smart Assign',
      onSelect: () => applyBulkTarget(selectedAliases, 'SMART_ASSIGN'),
      disabled: hasUnknownSelection || smartAssignPlan.alreadyAtTargetCount === selectedCount,
    },
    {
      label: smartConfigMode === SmartConfigMode.ALL_EXCLUDED ? 'Remove Smart Assign' : 'Exclude',
      onSelect: () => applyBulkTarget(selectedAliases, 'EXCLUDED'),
      disabled: hasUnknownSelection || excludePlan.alreadyAtTargetCount === selectedCount,
    },
  ];

  return (
    <Card data-testid="smart-assign-header" css={{ marginBlock: '400', padding: '500' }}>
      <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '200' }}>
        <Box css={{ display: 'flex', alignItems: 'center', gap: '300' }}>
          <Text textStyle="heading-sm">{COPY.product.spotlight}</Text>
          <Box
            css={{
              display: 'flex',
              alignItems: 'baseline',
              paddingLeft: '300',
              borderLeft: '1px solid {colors.border.subtle}',
            }}
          >
            <Text
              textStyle={availableCount > 0 ? 'body-sm-bold' : 'body-sm'}
              asChild
              css={{ color: availableCount > 0 ? '{colors.text.accent.blue.strong}' : '{colors.text.subtle}' }}
            >
              <span>
                {availableCount} Available {availableCount === 1 ? 'placement' : 'placements'}
              </span>
            </Text>
            <Text textStyle="body-sm" asChild css={{ color: '{colors.text.subtle}' }}>
              <span>
                {smartAssignEnabled
                  ? ` · ${pinnedCount} Pinned · ${autoCount} Smart Assign`
                  : ` · ${inSpotlight} in Spotlight`}
              </span>
            </Text>
          </Box>
        </Box>
        <Box css={{ display: 'flex', alignItems: 'center', gap: '200' }}>
          <Text textStyle="body-sm-bold">{COPY.smartAssign.toggleLabel}</Text>
          <Switch
            controlId="smart-assign-toggle"
            aria-label={COPY.smartAssign.toggleLabel}
            checked={smartAssignEnabled}
            disabled={isDisablingSmartAssign || enableBlocked}
            onChange={handleToggle}
          />
          <Tooltip
            content={feedManagedBlocked ? COPY.smartAssign.feedManagedTooltip : COPY.smartAssign.tooltip}
            placement="top-end"
            trigger={INFO_ICON}
          />
        </Box>
      </Box>

      {smartAssignEnabled && (
        <>
          <Divider css={{ marginBlock: '400' }} />

          <Box css={{ display: 'flex', alignItems: 'center', gap: '300', flexWrap: 'wrap' }}>
            <Box css={{ display: 'flex', alignItems: 'center', gap: '300', flexShrink: 0 }}>
              <Tooltip content={COPY.tooltips.optimizeBy} trigger={INFO_ICON} />
              <Text textStyle="body-sm-bold" css={{ color: '{colors.text.neutral}' }}>
                Optimize by
              </Text>
              <DropdownSelect
                value={optimizer}
                options={options}
                onChange={(value) => onChangeOptimizer((Array.isArray(value) ? value[0] : value) as OptimizerId)}
                showLabelForValue
                size="sm"
                css={{ width: '220px' }}
              />
            </Box>

            {isSelectionMode ? (
              <Box css={{ display: 'flex', alignItems: 'center', gap: '200', marginInlineStart: 'auto' }}>
                <Text textStyle="body-sm">{selectedCount} selected</Text>
                <IconButton
                  title="Clear selection"
                  size="sm"
                  emphasis="bare"
                  shape="circle"
                  icon={<IconCloseFilled />}
                  onClick={clearSelection}
                />
                {selectedCount > 0 && bulkActions.length > 0 && (
                  <Menu
                    trigger={
                      <Button size="sm" emphasis="outlined">
                        Action ▾
                      </Button>
                    }
                    content={bulkActions.map(({ label, onSelect, disabled }) => (
                      <Menu.Item key={label} onClick={disabled ? undefined : onSelect} disabled={disabled}>
                        <Menu.ItemLabel>{label}</Menu.ItemLabel>
                      </Menu.Item>
                    ))}
                  />
                )}
              </Box>
            ) : (
              <Button size="sm" emphasis="outlined" onClick={toggleSelectionMode} css={{ marginInlineStart: 'auto' }}>
                Multi-select
              </Button>
            )}
          </Box>
        </>
      )}

      {activationOpen && (
        <SmartAssignActivationModal
          open
          onClose={() => setActivationOpen(false)}
          inUse={inSpotlight}
          total={licenses}
        />
      )}
    </Card>
  );
}

export default SmartAssignHeader;
